import { IconChevronDown, IconMinus, IconTrendingDown, IconTrendingUp } from "@tabler/icons-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Menu, MenuPopup, MenuRadioGroup, MenuRadioItem, MenuTrigger } from "@/components/ui/menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { kpiChangeLabel, kpiTone, type PerformanceKpi } from "./performance";
import { formatKpiValue } from "./performance-format";

/**
 * The card carries two independent controls, so it cannot itself be a button:
 * a full-bleed click layer that focuses the chart on this metric, and a metric
 * menu in the label. The click layer sits underneath and the content layer is
 * pointer-transparent apart from the menu trigger.
 */
export function KpiCard<M extends string>({
  showPreviousValue,
  kpi,
  selected,
  selectionColor,
  slotIndex,
  metrics,
  takenMetrics,
  onClick,
  onSlotChange,
  getMetricLabel,
  size,
  className,
}: {
  showPreviousValue: boolean;
  kpi: PerformanceKpi<M>;
  selected: boolean;
  selectionColor?: string | null;
  slotIndex: number;
  metrics: readonly M[];
  takenMetrics: readonly M[];
  onClick: () => void;
  onSlotChange: (index: number, metric: M) => void;
  getMetricLabel?: (key: M) => string;
  size: "compact" | "default";
  className?: string;
}) {
  const { t } = useTranslation("campaign");
  const { formatCurrency, formatNumber, formatPercent } = useFormatters();
  const label = getMetricLabel?.(kpi.key) ?? kpi.label;
  const tone = kpiTone(kpi);
  const changeLabel = kpi.loading ? "—" : kpiChangeLabel(kpi, t);
  const descriptionId = useId();
  const value = formatKpiValue(
    kpi.value,
    kpi.format,
    {
      formatCurrency,
      formatNumber,
      formatPercent,
    },
    kpi.currency,
  );

  return (
    <div
      className={cn(
        "relative flex min-w-0 flex-col justify-center border-line px-4",
        selectionColor !== undefined && "justify-start pt-3",
        size === "default" ? "h-28" : "h-[94px]",
        selected &&
          selectionColor === undefined &&
          "bg-control-highlight after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-primary",
        className,
      )}
    >
      {selectionColor !== undefined ? (
        <span
          aria-hidden
          className="absolute inset-x-3 top-0 h-1 rounded-b"
          style={{ backgroundColor: selectionColor ?? "var(--color-subtle)" }}
        />
      ) : null}
      <button
        type="button"
        aria-pressed={selected}
        aria-describedby={kpi.description ? descriptionId : undefined}
        disabled={kpi.loading}
        aria-label={t("performance.chartAria", {
          label,
          value,
          change: changeLabel,
        })}
        className="absolute inset-0 outline-none transition-colors hover:bg-subtle/60 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        onClick={onClick}
      />
      <div className="pointer-events-none relative flex min-w-0 flex-col">
        <MetricSlotMenu
          kpi={kpi}
          label={label}
          slotIndex={slotIndex}
          metrics={metrics}
          takenMetrics={takenMetrics}
          onSlotChange={onSlotChange}
          getMetricLabel={getMetricLabel}
          size={size}
        />
        <span
          className={cn(
            "mt-1 truncate font-mono font-semibold tabular-nums text-ink",
            size === "default" ? "text-2xl leading-7 tracking-tight" : "text-[18px] leading-6",
          )}
        >
          {kpi.loading ? <Skeleton className="h-7 w-24" /> : value}
        </span>
        <span
          className={cn(
            "mt-0.5 flex items-center gap-1 truncate",
            size === "default" ? "text-xs leading-4" : "text-[9px]",
            tone === "success" && "text-success-foreground",
            tone === "destructive" && "text-destructive-foreground",
            tone === "muted" && "text-muted-foreground",
          )}
        >
          {showPreviousValue && !kpi.loading ? (
            <span
              data-slot="previous-value"
              className="mr-1 shrink-0 font-mono tabular-nums text-muted-foreground"
            >
              {formatKpiValue(
                kpi.previousValue,
                kpi.format,
                { formatCurrency, formatNumber, formatPercent },
                kpi.currency,
              )}
            </span>
          ) : null}
          {kpi.direction === "up" ? (
            <IconTrendingUp
              className={cn("shrink-0", size === "default" ? "size-3.5" : "size-3")}
              aria-hidden
            />
          ) : kpi.direction === "down" ? (
            <IconTrendingDown
              className={cn("shrink-0", size === "default" ? "size-3.5" : "size-3")}
              aria-hidden
            />
          ) : (
            <IconMinus
              className={cn("shrink-0", size === "default" ? "size-3.5" : "size-3")}
              aria-hidden
            />
          )}
          <span className="truncate">{changeLabel}</span>
        </span>
        {kpi.description ? (
          <span
            id={descriptionId}
            title={kpi.description}
            className="mt-1 truncate text-[10px] text-muted-foreground"
          >
            {kpi.description}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MetricSlotMenu<M extends string>({
  kpi,
  label,
  slotIndex,
  metrics,
  takenMetrics,
  onSlotChange,
  getMetricLabel,
  size,
}: {
  kpi: PerformanceKpi<M>;
  label: string;
  slotIndex: number;
  metrics: readonly M[];
  takenMetrics: readonly M[];
  onSlotChange: (index: number, metric: M) => void;
  getMetricLabel?: (key: M) => string;
  size: "compact" | "default";
}) {
  const { t } = useTranslation("campaign");
  return (
    <Menu>
      <MenuTrigger
        aria-label={t("performance.slotMenuAria", {
          index: slotIndex + 1,
          label,
        })}
        className={cn(
          "pointer-events-auto -mx-1 flex w-fit max-w-full items-center gap-0.5 rounded-[4px] px-1 py-0.5 font-medium uppercase tracking-[0.04em] text-muted-foreground outline-none transition-colors hover:bg-subtle hover:text-ink focus-visible:ring-2 focus-visible:ring-ring aria-expanded:bg-subtle aria-expanded:text-ink",
          size === "default" ? "text-xs" : "text-[9px]",
        )}
      >
        <span className="truncate">{label}</span>
        <IconChevronDown className="size-3 shrink-0" aria-hidden />
      </MenuTrigger>
      <MenuPopup align="start" className="w-44">
        <MenuRadioGroup
          value={kpi.key}
          onValueChange={(next) => onSlotChange(slotIndex, next as M)}
        >
          {metrics.map((option) => {
            // A metric already on another card is greyed out; each metric can
            // occupy at most one card.
            const shownElsewhere = option !== kpi.key && takenMetrics.includes(option);
            return (
              <MenuRadioItem
                key={option}
                value={option}
                disabled={shownElsewhere}
                className={size === "default" ? "text-sm" : "text-[11px]"}
              >
                {getMetricLabel?.(option) ??
                  t(`performance.metricMenu.${option}`, { defaultValue: option })}
              </MenuRadioItem>
            );
          })}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}
